/* eslint-disable @angular-eslint/directive-selector */
import { ChangeDetectionStrategy, Component, ContentChild, Directive, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges, TemplateRef } from '@angular/core';
import { FormControl } from '@angular/forms';
import { NgbPaginationConfig } from './pagination-config';
import { getValueInRange, isNumber } from './util';

export interface NgbPaginationLinkContext {
    currentPage: number;
    disabled: boolean;
}

export interface NgbPaginationNumberContext extends NgbPaginationLinkContext {
    $implicit: number;
}

export interface NgbPaginationPagesContext {
    $implicit: number;
    disabled: boolean;
    pages: number[];
}

@Directive({ selector: 'ng-template[ngbPaginationEllipsis]' })
export class NgbPaginationEllipsis {
    templateRef = inject(TemplateRef<NgbPaginationLinkContext>);
}

@Directive({ selector: 'ng-template[ngbPaginationFirst]' })
export class NgbPaginationFirst {
    templateRef = inject(TemplateRef<NgbPaginationLinkContext>);
}

@Directive({ selector: 'ng-template[ngbPaginationLast]' })
export class NgbPaginationLast {
    templateRef = inject(TemplateRef<NgbPaginationLinkContext>);
}

@Directive({ selector: 'ng-template[ngbPaginationNext]' })
export class NgbPaginationNext {
    templateRef = inject(TemplateRef<NgbPaginationLinkContext>);
}

@Directive({ selector: 'ng-template[ngbPaginationNumber]' })
export class NgbPaginationNumber {
    templateRef = inject(TemplateRef<NgbPaginationNumberContext>);
}

@Directive({ selector: 'ng-template[ngbPaginationPrevious]' })
export class NgbPaginationPrevious {
    templateRef = inject(TemplateRef<NgbPaginationLinkContext>);
}

@Directive({ selector: 'ng-template[ngbPaginationPages]' })
export class NgbPaginationPages {
    templateRef = inject(TemplateRef<NgbPaginationPagesContext>);
}

@Component({
    selector: 'lokos-pagination',
    templateUrl: './pagination.component.html',
    styleUrls: ['./pagination.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class PaginationComponent implements OnChanges {
    private _config = inject(NgbPaginationConfig);

    @ContentChild(NgbPaginationEllipsis, { static: false }) tplEllipsis?: NgbPaginationEllipsis;
    @ContentChild(NgbPaginationFirst, { static: false }) tplFirst?: NgbPaginationFirst;
    @ContentChild(NgbPaginationLast, { static: false }) tplLast?: NgbPaginationLast;
    @ContentChild(NgbPaginationNext, { static: false }) tplNext?: NgbPaginationNext;
    @ContentChild(NgbPaginationNumber, { static: false }) tplNumber?: NgbPaginationNumber;
    @ContentChild(NgbPaginationPrevious, { static: false }) tplPrevious?: NgbPaginationPrevious;
    @ContentChild(NgbPaginationPages, { static: false }) tplPages?: NgbPaginationPages;

    @Input() boundaryLinks = false;
    @Input() directionLinks = true;
    @Input() disabled = false;
    @Input() ellipses = true;
    @Input() maxSize = 5;
    @Input() page = 1;
    @Input() pageSize = 10;
    @Input() rotate = false;
    @Input({ required: true }) collectionSize!: number;
    @Input() size!: 'sm' | 'lg' | string | null;
    @Input() pageSizeOptions = [10, 25, 50, 100];

    @Output() pageChange = new EventEmitter<number>(true);
    @Output() pageSizeChange = new EventEmitter<any>(true);

    pageCount = 0;
    pages: number[] = [];
    pageSizeControl = new FormControl(this.pageSize);

    hasPrevious (): boolean {
        return this.page > 1;
    }

    hasNext (): boolean {
        return this.page < this.pageCount;
    }

    nextDisabled (): boolean {
        return !this.hasNext() || this.disabled;
    }

    previousDisabled (): boolean {
        return !this.hasPrevious() || this.disabled;
    }

    selectPage (pageNumber: number): void {
        this._updatePages(pageNumber);
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ngOnChanges (changes: SimpleChanges): void {
        this._updatePages(this.page);
    }

    isEllipsis (pageNumber: any): boolean {
        return pageNumber === -1;
    }

    private _applyEllipses (start: number, end: number) {
        if (this.ellipses) {
            if (start > 0) {
                if (start > 2) {
                    this.pages.unshift(-1);
                } else if (start === 2) {
                    this.pages.unshift(2);
                }
                this.pages.unshift(1);
            }
            if (end < this.pageCount) {
                if (end < this.pageCount - 2) {
                    this.pages.push(-1);
                } else if (end === this.pageCount - 2) {
                    this.pages.push(this.pageCount - 1);
                }
                this.pages.push(this.pageCount);
            }
        }
    }

    private _applyRotation (): [number, number] {
        let start = 0;
        let end = this.pageCount;
        const leftOffset = Math.floor(this.maxSize / 2);
        const rightOffset = this.maxSize % 2 === 0 ? leftOffset - 1 : leftOffset;

        if (this.page <= leftOffset) {
            end = this.maxSize;
        } else if (this.pageCount - this.page < leftOffset) {
            start = this.pageCount - this.maxSize;
        } else {
            start = this.page - leftOffset - 1;
            end = this.page + rightOffset;
        }

        return [start, end];
    }

    private _applyPagination (): [number, number] {
        const page = Math.ceil(this.page / this.maxSize) - 1;
        const start = page * this.maxSize;
        const end = start + this.maxSize;

        return [start, end];
    }

    private _setPageInRange (newPageNo: any) {
        const prevPageNo = this.page;
        this.page = getValueInRange(newPageNo, this.pageCount, 1);

        if (this.page !== prevPageNo && isNumber(this.collectionSize)) {
            this.pageChange.emit(this.page - 1);
        }
    }

    private _updatePages (newPage: number) {
        this.pageCount = Math.ceil(this.collectionSize / this.pageSize);

        if (!isNumber(this.pageCount)) {
            this.pageCount = 0;
        }

        this.pages.length = 0;
        for (let i = 1; i <= this.pageCount; i++) {
            this.pages.push(i);
        }

        this._setPageInRange(newPage);

        if (this.maxSize > 0 && this.pageCount > this.maxSize) {
            let start = 0;
            let end = this.pageCount;

            if (this.rotate) {
                [start, end] = this._applyRotation();
            } else {
                [start, end] = this._applyPagination();
            }

            this.pages = this.pages.slice(start, end);

            this._applyEllipses(start, end);
        }
    }

    get startItem (): number {
        if (!this.collectionSize) return 0;
        return (this.page - 1) * this.pageSize + 1;
    }

    get endItem (): number {
        if (!this.collectionSize) return 0;
        return Math.min(this.page * this.pageSize, this.collectionSize);
    }

    onPageSizeChange (pageSizeChange: number) {
        this.pageSize = pageSizeChange;
        this.pageSizeControl.setValue(this.pageSize, { emitEvent: false });
        this.pageSizeChange.emit({target:{value:this.pageSize}});
    }
}
